import { format } from 'date-fns';
import type {
  EngageDisconnectedReason,
  EngageDomainCheck,
  EngageMailbox,
  EngagePausedReason,
} from '@/store/features/engage.feature';
import { domainProblems } from '../sending-domain.util';

const PAUSED_FOR: Record<EngagePausedReason, string> = {
  bounce_rate: 'bounce rate',
  linkedin_limit: 'LinkedIn limit',
  linkedin_restricted: 'LinkedIn restricted',
  linkedin_warning: 'LinkedIn warning',
};

/** Why the system paused a connection, in words. */
export function pausedFor(reason: EngagePausedReason): string {
  return PAUSED_FOR[reason] ?? 'paused';
}

/**
 * "Paused: bounce rate" for a pause that waits for someone to resume it,
 * "Paused until Oct 10" for one that lifts on its own; null while the
 * connection may send, including once a timed pause has passed.
 */
export function pauseLabel(
  mailbox: Pick<EngageMailbox, 'paused_reason' | 'paused_until'>,
  now: Date = new Date()
): string | null {
  if (!mailbox.paused_reason) return null;
  if (!mailbox.paused_until)
    return 'Paused: ' + pausedFor(mailbox.paused_reason);
  const until = new Date(mailbox.paused_until);
  if (Number.isNaN(until.getTime())) return 'Paused';
  if (until.getTime() <= now.getTime()) return null;
  return 'Paused until ' + format(until, 'MMM d');
}

const TURNED_OFF: Record<
  EngageDisconnectedReason,
  { words: string; detail: string }
> = {
  plan_ended: {
    words: 'plan ended',
    detail:
      'Turned off when the plan ended. Its sequences and history are kept.',
  },
  free_unused: {
    words: 'no sends in 7 days',
    detail:
      'A mailbox on the Free plan turns off after 7 days without a send. Its history is kept.',
  },
  unused: {
    words: 'unused for 30 days',
    detail:
      'Turned off after 30 days with nothing sent or received. Its sequences and history are kept.',
  },
  failed: {
    words: 'sign-in failed',
    detail:
      'Its sign-in stopped working and wasn’t fixed within 7 days. Its sequences and history are kept.',
  },
  linkedin_requires_pro: {
    words: 'LinkedIn needs Pro',
    detail:
      'LinkedIn accounts connect on Pro and up. Its sequences and history are kept.',
  },
};

/**
 * Why the system turned a connection off, in words, or null: it is connected,
 * it lost its sign-in some other way, or the reason is one this build doesn't
 * know.
 */
export function turnedOff(
  mailbox: Pick<EngageMailbox, 'connected' | 'disconnected_reason'>
): { label: string; words: string; detail: string } | null {
  if (mailbox.connected || !mailbox.disconnected_reason) return null;
  const known = Object.prototype.hasOwnProperty.call(
    TURNED_OFF,
    mailbox.disconnected_reason
  );
  if (!known) return null;
  const { words, detail } = TURNED_OFF[mailbox.disconnected_reason];
  return { label: 'Off: ' + words, words, detail };
}

/**
 * What keeps a mailbox from sending, in a word, with the fuller reason of a
 * pause as its title; null when nothing does. A connection that lost its
 * sign-in reads as one to reconnect, whatever else is true of it.
 */
export function sendBlock(
  mailbox: Pick<
    EngageMailbox,
    | 'connected'
    | 'active'
    | 'paused_reason'
    | 'paused_until'
    | 'disconnected_reason'
  >,
  now: Date = new Date()
): { label: string; title?: string } | null {
  const off = turnedOff(mailbox);
  if (off) return { label: off.label, title: off.detail };
  if (!mailbox.connected) return { label: 'Reconnect required' };
  if (!mailbox.active) return { label: 'Turned off' };
  const paused = pauseLabel(mailbox, now);
  return paused ? { label: 'Paused', title: paused } : null;
}

/** What a domain check found, for the toast after Check domain. */
export function domainResult(check: EngageDomainCheck | null): string {
  if (!check) return 'Domain not checked';
  if (check.status === 'provider') return 'Personal email domain';
  const problems = domainProblems(check);
  return problems.length ? problems.join(', ') : 'SPF, DKIM and DMARC found';
}
